import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LuChartColumn, LuGraduationCap, LuLoaderCircle, LuTriangleAlert, LuUserRoundCheck, LuUsers } from 'react-icons/lu';
import { collection, getDocs, doc, query, where, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import * as XLSX from 'xlsx';
import {
  isArchivedAssignment,
  formatUnassignedDate,
} from '../utils/assignmentHistory';
import { matchesSearchTerm } from '../utils/searchText';
import { fetchMissingRequiredDocumentsByEmployee } from '../utils/cvDocuments';
import { toast } from 'react-toastify';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import ProjectVisitsModal from './ProfilePage/ProjectVisitsModal';
import { CARD, EmptyState, HelpNote } from './ProfilePage/components/ProfileUi';
import ManualDataEntryModal from './SMOLeadReports/ManualDataEntryModal';
import IssueDetailModal from './SMOLeadReports/IssueDetailModal';
import EmployeeDocumentsModal from './SMOLeadReports/EmployeeDocumentsModal';
import TeamActivityPanel from './SMOLeadReports/TeamActivityPanel';
import { useTeamActivity } from './SMOLeadReports/useTeamActivity';
import TeamTrainingPanel from './SMOLeadReports/TeamTrainingPanel';
import { useTeamTraining } from './SMOLeadReports/useTeamTraining';
import useSmoText from './SMOLeadReports/smoText';
import { SmoHeader, SmoStats } from './SMOLeadReports/parts/SmoHeader';
import SmoPicker from './SMOLeadReports/parts/SmoPicker';
import SmoCenters from './SMOLeadReports/parts/SmoCenters';
import WorkerFilters from './SMOLeadReports/parts/WorkerFilters';
import WorkersTable from './SMOLeadReports/parts/WorkersTable';
import ReportSection, { DateRangeBar, SectionStatus } from './SMOLeadReports/parts/ReportSection';
import OperationalOverview from './SMOLeadReports/parts/OperationalOverview';
import QualityRiskPanel from './SMOLeadReports/parts/QualityRiskPanel';
import TeamPerformance from './SMOLeadReports/parts/TeamPerformance';
import IncompleteTasksDialog from './SMOLeadReports/parts/IncompleteTasksDialog';

/**
 * SMO Lead Raporları. Admins pick an SMO; SMO Leads see their own team (and the
 * views they get as a backup). Below the team table come five collapsible
 * reports, each with its own date range: Operasyonel Genel Bakış, Kalite &
 * Klinik Risk, Ekip Performansı, Sistem Kullanımı and Eğitim / SOP Takibi.
 * The data logic below is unchanged; the screen is built from ./SMOLeadReports/parts.
 */
const SMOLeadReports = () => {
  const { employeeId, employeeData, userAuthorization } = useUserProfile();
  const navigate = useNavigate();
  const { label, language } = useSmoText();
  // Toasts inside effects / callbacks read the current language through a ref,
  // so switching the language doesn't refetch anything
  const labelRef = useRef(label);
  labelRef.current = label;
  
  // SMO list state
  const [smoList, setSmoList] = useState([]);
  const [selectedSMO, setSelectedSMO] = useState(null);
  const [loadingSMOs, setLoadingSMOs] = useState(true);

  // Delegated SMO views for users assigned as "backup" SMO Lead on a project.
  // Each entry mirrors the primer SMO Lead's view but restricted to that project.
  const [delegatedSmoViews, setDelegatedSmoViews] = useState([]);

  // Delegated SMO views for users set as the "Yedek SMO" (backup) of one or
  // more centers in Merkez Yönetimi. Each entry mirrors the center's primer SMO
  // Lead view, scoped to just the centers where this user is the backup.
  const [centerBackupViews, setCenterBackupViews] = useState([]);
  
  // Workers under selected SMO
  const [workers, setWorkers] = useState([]);
  // CV/GCP/IATA belgeleri görüntülenen çalışan (Çalışan Yönetimi'ndeki
  // "CV/GCP/IATA Belgeleri" bölümüyle aynı kaynak, salt okunur)
  const [documentsEmployee, setDocumentsEmployee] = useState(null);
  // Çalışan bazında eksik zorunlu belgeler: { [employeeId]: [tür, ...] }
  const [missingDocsByWorker, setMissingDocsByWorker] = useState({});
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  
  // Centers for the selected SMO
  const [smoCenters, setSmoCenters] = useState([]);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive', 'incomplete'
  const [primerFilter, setPrimerFilter] = useState('all'); // 'all', 'primer', 'backup'
  const [blindedFilter, setBlindedFilter] = useState('all'); // 'all', 'blinded', 'unblinded'
  const [hideFinished, setHideFinished] = useState(false); // hide finished projects
  
  // Table scroll state
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tableContainerRef = useRef(null);
  
  // Modal state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showVisitsModal, setShowVisitsModal] = useState(false);

  // Expanded employee rows (to show visit summary inline)
  const [expandedWorkers, setExpandedWorkers] = useState({});

  // Collapsed cities state - tracks which cities are collapsed
  const [collapsedCities, setCollapsedCities] = useState({});

  // All visits for selected SMO's workers
  const [allVisits, setAllVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);

  // All calendar jobs for selected SMO's workers
  const [allCalendarJobs, setAllCalendarJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // Issue Detail modal — opened by clicking any "issue" indicator across the
  // dashboard sections. Shows what the issue was, who caused it and when.
  const [issueModal, setIssueModal] = useState(null); // { title, visits } | null

  // Collapsible dashboard sections — collapsed by default to avoid rendering heavy charts
  const [expandedSections, setExpandedSections] = useState({
    issueTracking: false,
    hastaneKpi: false,
    employeePerf: false,
    teamActivity: false,
    teamTraining: false,
  });
  const toggleSection = useCallback((key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Check if user is an admin or has full-view access via email
  const userEmail = employeeData?.eMail?.toLowerCase() || '';
  const FULL_VIEW_EMAILS = [
    'arzu.dereli@habcarecro.com',
    'hulya.bayraktar@habcarecro.com',
    'nagihan.cicisarac@habcarecro.com',
    'unzile.yetkin@habcarecro.com'
  ];
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin') || FULL_VIEW_EMAILS.includes(userEmail);

  // SMO removal and worker removal permission: only super admin (turanemre1999@gmail.com) and arzu.dereli
  const canRemoveSMO = userEmail === 'turanemre1999@gmail.com' || userEmail === 'arzu.dereli@habcarecro.com';

  // Manual data entry permission: only admins and unzile.yetkin
  const MANUAL_ENTRY_EMAILS = ['unzile.yetkin@habcarecro.com'];
  const canManualEntry = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin') || MANUAL_ENTRY_EMAILS.includes(userEmail);

  // Manual data entry modal state
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Incomplete tasks modal state
  const [showIncompleteTasksModal, setShowIncompleteTasksModal] = useState(false);

  // ── Date range filters for dashboard sections ─────────────────────────
  const getMonthStart = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const getToday = () => new Date().toISOString().split('T')[0];

  const [s1DateStart, setS1DateStart] = useState(getMonthStart);
  const [s1DateEnd, setS1DateEnd] = useState(getToday);
  const [s2DateStart, setS2DateStart] = useState(getMonthStart);
  const [s2DateEnd, setS2DateEnd] = useState(getToday);
  const [s3DateStart, setS3DateStart] = useState(getMonthStart);
  const [s3DateEnd, setS3DateEnd] = useState(getToday);
  const [s4DateStart, setS4DateStart] = useState(getMonthStart);
  const [s4DateEnd, setS4DateEnd] = useState(getToday);

  const filterVisitsByDate = useCallback((visits, start, end) => {
    if (!visits) return [];
    return visits.filter(v => {
      const d = v.date || v.firstVisitDate || '';
      if (!d) return false;
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
  }, []);

  const s1Visits = useMemo(() => filterVisitsByDate(allVisits, s1DateStart, s1DateEnd), [allVisits, s1DateStart, s1DateEnd, filterVisitsByDate]);
  const s2Visits = useMemo(() => filterVisitsByDate(allVisits, s2DateStart, s2DateEnd), [allVisits, s2DateStart, s2DateEnd, filterVisitsByDate]);
  const s3Visits = useMemo(() => filterVisitsByDate(allVisits, s3DateStart, s3DateEnd), [allVisits, s3DateStart, s3DateEnd, filterVisitsByDate]);

  const resetDateFilter = useCallback((section) => {
    const start = getMonthStart();
    const end = getToday();
    if (section === 1) { setS1DateStart(start); setS1DateEnd(end); }
    else if (section === 2) { setS2DateStart(start); setS2DateEnd(end); }
    else if (section === 3) { setS3DateStart(start); setS3DateEnd(end); }
    else if (section === 4) { setS4DateStart(start); setS4DateEnd(end); }
  }, []);

  const setAllTime = useCallback((section) => {
    if (section === 1) { setS1DateStart(''); setS1DateEnd(''); }
    else if (section === 2) { setS2DateStart(''); setS2DateEnd(''); }
    else if (section === 3) { setS3DateStart(''); setS3DateEnd(''); }
    else if (section === 4) { setS4DateStart(''); setS4DateEnd(''); }
  }, []);

  // ── Ekip sistem kullanımı (çevrimiçi süre / giriş istatistikleri) ──────
  // Nabız verisi Sayfa Yetkileri'ndeki "Çevrimiçi Olanlar" ile aynı kaynaktan
  // okunur; burada yalnızca seçili SMO'nun ekibine daraltılır. Geçmiş veriler
  // bölüm açılana kadar çekilmez (`enabled`), canlı durum ise başlıktaki
  // çevrimiçi sayısı için her zaman dinlenir.
  const teamActivity = useTeamActivity(workers, {
    enabled: expandedSections.teamActivity,
    startDate: s4DateStart,
    endDate: s4DateEnd,
  });

  // ── Ekip eğitim / SOP takibi ───────────────────────────────────────────
  // Bölüm kapalıyken de yüklenir: başlıktaki "N çalışan tamamlamadı" rozeti
  // uyarının görevini görüyor, açılmasını beklemek uyarıyı gizlerdi.
  const teamTraining = useTeamTraining(workers);

  // ── Incomplete Tasks computation ────────────────────────────────
  const incompleteTasks = useMemo(() => {
    if (!allVisits && !allCalendarJobs) return { visits: [], jobs: [], total: 0, byWorker: {} };

    // Get incomplete visits (status: false or undefined)
    const incompleteVisits = (allVisits || []).filter(visit => !visit.status).map(visit => {
      const worker = workers.find(w => w.id === visit.employeeId);
      return {
        ...visit,
        type: 'visit',
        workerName: worker ? `${worker.name} ${worker.surname}` : (visit.employeeName || 'Bilinmeyen Çalışan'),
        title: `${visit.patientId || 'Hasta'} - ${visit.visitType === 'home' ? 'Ev Viziti' : 'Hastane Viziti'}`,
        date: visit.firstVisitDate || visit.date
      };
    });

    // Get incomplete calendar jobs (status: false or undefined)
    const incompleteJobs = (allCalendarJobs || []).filter(job => !job.status).map(job => {
      const worker = workers.find(w => w.id === job.employeeId);
      return {
        ...job,
        type: 'job',
        workerName: worker ? `${worker.name} ${worker.surname}` : 'Bilinmeyen Çalışan',
        date: job.startDate
      };
    });

    // Combine and sort by date
    const allIncompleteTasks = [...incompleteVisits, ...incompleteJobs].sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });

    // Group by worker ID
    const byWorker = {};
    allIncompleteTasks.forEach(task => {
      const workerId = task.employeeId;
      if (!byWorker[workerId]) {
        byWorker[workerId] = [];
      }
      byWorker[workerId].push(task);
    });

    return {
      visits: incompleteVisits,
      jobs: incompleteJobs,
      all: allIncompleteTasks,
      total: allIncompleteTasks.length,
      byWorker: byWorker
    };
  }, [allVisits, allCalendarJobs, workers]);

  // Tamamlanmamış vizitlerin (status false/undefined) çalışan ve çalışan+proje
  // kırılımındaki sayıları. Satırdaki ve proje kartındaki küçük belirteçler
  // bunu kullanır; böylece vizit modalını açmadan eksik olan görülür.
  //
  // Sayaçlar vizit modalıyla birebir tutmalı: modal tarihi olmayan kayıtları
  // listelemediğinden (bkz. ProjectVisitsModal) burada da tarihsizler sayılmaz.
  const incompleteVisitCounts = useMemo(() => {
    const byWorkerProject = {};
    (allVisits || []).forEach((visit) => {
      if (visit.status) return;
      if (!visit.date || String(visit.date).trim() === '') return;
      const key = `${visit.employeeId}|${visit.projectId}`;
      byWorkerProject[key] = (byWorkerProject[key] || 0) + 1;
    });

    // Satırdaki rozet, satır açıldığında görünecek proje kartlarının
    // toplamıdır; çalışanın bu listede yer almayan projelerindeki vizitler
    // sayılmaz. Aynı proje birden fazla atama satırıyla gelebildiği için
    // projectId'ler tekilleştirilir.
    const byWorker = {};
    workers.forEach((worker) => {
      const projectIds = [...new Set((worker.projects || []).map((p) => p.projectId))];
      byWorker[worker.id] = projectIds.reduce(
        (sum, projectId) => sum + (byWorkerProject[`${worker.id}|${projectId}`] || 0),
        0
      );
    });

    return { byWorker, byWorkerProject };
  }, [allVisits, workers]);

  // ── Ev Viziti Sorun Takibi computation ────────────────────────────────
  const capaDashboard = useMemo(() => {
    if (!s1Visits || s1Visits.length === 0) return null;

    const homeVisits = s1Visits.filter(v => v.visitType === 'home');
    if (homeVisits.length === 0) return null;

    const coordinatorMap = {};

    homeVisits.forEach(v => {
      const empId = v.employeeId;
      const w = workers.find(wk => wk.id === empId);
      const empName = w ? `${w.name} ${w.surname}` : (v.employeeName || empId);

      if (!coordinatorMap[empId]) {
        coordinatorMap[empId] = {
          id: empId,
          name: empName,
          totalVisits: 0,
          totalIssues: 0,
          missedVisits: 0,
          aeSaeCount: 0,
          dcfCreatedCount: 0,
          visits: [],
          monthlyIssues: {},
        };
      }

      const c = coordinatorMap[empId];
      c.totalVisits++;
      c.visits.push(v);

      const ev = v.visitEvaluation || {};
      const vDate = v.date || v.firstVisitDate || '';
      const month = vDate ? vDate.substring(0, 7) : null;

      if (ev.issueOccurred === 'yes') {
        c.totalIssues++;
        if (month) {
          if (!c.monthlyIssues[month]) c.monthlyIssues[month] = { issues: 0, missed: 0 };
          c.monthlyIssues[month].issues++;
        }
      }
      if (ev.issueResultedInMiss === 'yes') {
        c.missedVisits++;
        if (month && c.monthlyIssues[month]) c.monthlyIssues[month].missed++;
      }
      if (ev.wasAeSaeTaken === 'yes') c.aeSaeCount++;
      if (ev.dcfCreated === 'yes') c.dcfCreatedCount++;
    });

    const coordinators = Object.values(coordinatorMap).sort((a, b) => a.name.localeCompare(b.name));

    const totals = {
      totalVisits: homeVisits.length,
      totalIssues: coordinators.reduce((s, c) => s + c.totalIssues, 0),
      missedVisits: coordinators.reduce((s, c) => s + c.missedVisits, 0),
      aeSaeCount: coordinators.reduce((s, c) => s + c.aeSaeCount, 0),
      dcfCreatedCount: coordinators.reduce((s, c) => s + c.dcfCreatedCount, 0),
    };

    // Bar chart: issues vs missed per coordinator
    const barChartData = coordinators.filter(c => c.totalIssues > 0 || c.missedVisits > 0).map(c => ({
      name: c.name.length > 15 ? c.name.substring(0, 14) + '…' : c.name,
      fullName: c.name,
      Sorun: c.totalIssues,
      Kaçırılan: c.missedVisits,
    }));

    // Pie chart: issue breakdown
    const pieChartData = [
      { name: 'Sorunlu Vizit', value: totals.totalIssues },
      { name: 'Sorunsuz Vizit', value: Math.max(0, totals.totalVisits - totals.totalIssues) },
    ].filter(d => d.value > 0);

    // Trend data (monthly)
    const allMonths = {};
    coordinators.forEach(c => {
      Object.entries(c.monthlyIssues).forEach(([month, data]) => {
        if (!allMonths[month]) allMonths[month] = { month, Sorun: 0, Kaçırılan: 0 };
        allMonths[month]['Sorun'] += data.issues;
        allMonths[month]['Kaçırılan'] += data.missed;
      });
    });
    const trendData = Object.values(allMonths).sort((a, b) => a.month.localeCompare(b.month));

    // AE/SAE & DCF overview
    const extrasData = [
      { name: 'AE/SAE Alınan', value: totals.aeSaeCount },
      { name: 'DCF Oluşturulan', value: totals.dcfCreatedCount },
    ].filter(d => d.value > 0);

    return { coordinators, totals, barChartData, pieChartData, trendData, extrasData };
  }, [s1Visits, workers]);

  // ── Issue drill-down helpers ───────────────────────────────────────────
  // Filter a pool of visits down to the ones matching a given "issue" metric.
  const filterVisitsByMetric = useCallback((pool, metric) => {
    return (pool || []).filter(v => {
      const ev = v.visitEvaluation || {};
      switch (metric) {
        case 'issues': return ev.issueOccurred === 'yes';
        case 'missed': return ev.issueResultedInMiss === 'yes';
        case 'deviation': return ev.protocolDeviation === 'yes';
        case 'capa': return ev.capaOpened === 'yes';
        case 'screenFail': return ev.screenFail === 'yes';
        case 'dropPatient': return ev.dropPatient === 'yes';
        case 'aesae': return ev.wasAeSaeTaken === 'yes';
        case 'dcf': return ev.dcfCreated === 'yes';
        case 'missingProcessEnd': return !ev.processEndDate;
        case 'overdue': {
          const start = v.date || v.firstVisitDate;
          if (!ev.slaTarget || !ev.processEndDate || !start) return false;
          const cycle = Math.ceil((new Date(ev.processEndDate) - new Date(start)) / 86400000);
          return cycle > parseFloat(ev.slaTarget);
        }
        default: return false;
      }
    });
  }, []);

  // Open the Issue Detail modal with a pre-filtered visit list.
  const openIssueModal = useCallback((title, pool, metric) => {
    const visits = metric ? filterVisitsByMetric(pool, metric) : (pool || []);
    if (visits.length === 0) return; // nothing to inspect
    setIssueModal({ title, visits });
  }, [filterVisitsByMetric]);

  // Authoritative projectId -> short title map, fetched once from the Projects
  // collection. Visits only store the Firestore projectId, so we join here.
  const [projectTitleMap, setProjectTitleMap] = useState({});
  useEffect(() => {
    const fetchProjectTitles = async () => {
      try {
        const snap = await getDocs(collection(db, 'Projects'));
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data();
          map[d.id] = data.titleShort || data.titleLong || '';
        });
        setProjectTitleMap(map);
      } catch (e) {
        console.error('Error fetching project titles:', e);
      }
    };
    fetchProjectTitles();
  }, []);

  // Combined projectId -> short title map. The Projects collection is primary;
  // the workers' assignments fill any gap (e.g. a project deleted from Projects).
  const projectNameMap = useMemo(() => {
    const map = { ...projectTitleMap };
    workers.forEach(w => (w.projects || []).forEach(p => {
      if (p.projectId && p.projectTitle && !map[p.projectId]) map[p.projectId] = p.projectTitle;
    }));
    return map;
  }, [projectTitleMap, workers]);

  // ── Hastane (Site) Visit KPI computation ────────────────────────────
  const hastaneKpis = useMemo(() => {
    if (!allVisits || allVisits.length === 0) return null;

    const siteVisits = allVisits.filter(v => v.visitType === 'hastane' || (v.visitType && v.visitType !== 'home'));
    if (siteVisits.length === 0) return null;

    const total = siteVisits.length;
    const homeVisits = allVisits.filter(v => v.visitType === 'home');

    const r = n => Math.round(n * 10) / 10;
    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;

    // Cycle times
    const cycleCalc = (arr) => {
      const vals = arr.flatMap(v => {
        const vDate = v.date || v.firstVisitDate;
        if (!vDate || !v.visitEvaluation?.processEndDate) return [];
        const days = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(vDate)) / 86400000);
        return days >= 0 ? [days] : [];
      });
      return vals.length > 0 ? r(vals.reduce((a, b) => a + b, 0) / vals.length) : '-';
    };
    const avgCycleTimeHome = cycleCalc(homeVisits);
    const avgCycleTimeSite = cycleCalc(siteVisits);
    const visitsMissingProcessEndDate = allVisits.filter(v => !v.visitEvaluation?.processEndDate).length;

    // Issues
    const issuesOccurred = siteVisits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length;
    const issueRate = pct(issuesOccurred, total);

    // Protocol deviations
    const protocolDeviations = siteVisits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
    const protocolDeviationRate = pct(protocolDeviations, total);

    // CAPA opened (from site visits)
    const capaOpenedCount = siteVisits.filter(v => v.visitEvaluation?.capaOpened === 'yes').length;

    // SLA
    const withSLA = siteVisits.filter(v => v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate);
    const slaMet = withSLA.filter(v => Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000) <= parseFloat(v.visitEvaluation.slaTarget)).length;
    const slaMetRate = pct(slaMet, withSLA.length);
    const overdueVisits = withSLA.length - slaMet;

    // Screen Fail
    const screenFailCount = siteVisits.filter(v => v.visitEvaluation?.screenFail === 'yes').length;
    const screenFailRate = pct(screenFailCount, total);

    // Drop Patient
    const dropPatientCount = siteVisits.filter(v => v.visitEvaluation?.dropPatient === 'yes').length;
    const dropPatientRate = pct(dropPatientCount, total);

    // Key Rates chart data
    const keyRatesChart = [
      { name: 'Tarama Başarısız', Oran: screenFailRate },
      { name: 'Hasta Çıkışı', Oran: dropPatientRate },
      { name: 'Protokol Sapması', Oran: protocolDeviationRate },
      { name: 'SLA Karşılandı', Oran: slaMetRate },
    ];

    return {
      total,
      avgCycleTimeHome, avgCycleTimeSite, visitsMissingProcessEndDate,
      issuesOccurred, issueRate,
      protocolDeviationRate, capaOpenedCount,
      slaMetRate, overdueVisits,
      screenFailCount, screenFailRate,
      dropPatientCount, dropPatientRate,
      keyRatesChart,
    };
  }, [allVisits]);

  // ── Employee Performance Stats ────────────────────────────────────────
  const employeeStats = useMemo(() => {
    if (!s3Visits || s3Visits.length === 0 || workers.length === 0) return null;

    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;
    const r1 = n => Math.round(n * 10) / 10;

    const stats = workers.map(w => {
      const wVisits = s3Visits.filter(v => v.employeeId === w.id);
      const total = wVisits.length;
      if (total === 0) return null;

      const homeV = wVisits.filter(v => v.visitType === 'home');
      const siteV = wVisits.filter(v => v.visitType !== 'home');

      // KM (only travelDistanceBefore — the field users can fill)
      const totalKm = wVisits.reduce((sum, v) => {
        const ev = v.visitEvaluation || {};
        return sum + (parseFloat(ev.travelDistanceBefore) || 0);
      }, 0);
      const avgKmPerVisit = r1(total > 0 ? totalKm / total : 0);

      // eCRF & Source completion
      const ecrfDone = wVisits.filter(v => v.visitEvaluation?.ecrfCompletionDate).length;
      const ecrfPct = pct(ecrfDone, total);
      const sourceDone = wVisits.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length;
      const sourcePct = pct(sourceDone, total);

      // Deviations
      const deviationCount = wVisits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
      const deviationPct = r1(total > 0 ? deviationCount / total * 100 : 0);

      // CAPA Opened (from site visits only — capaOpened is in the site visit form)
      const capaOpenedCount = wVisits.filter(v =>
        v.visitEvaluation?.capaOpened === 'yes'
      ).length;

      // Screen Fail / Drop Patient
      const screenFailCount = wVisits.filter(v => v.visitEvaluation?.screenFail === 'yes').length;
      const dropPatientCount = wVisits.filter(v => v.visitEvaluation?.dropPatient === 'yes').length;

      // SLA
      const withSLA = wVisits.filter(v =>
        v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate
      );
      const slaMet = withSLA.filter(v => {
        const cycle = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000);
        return cycle <= parseFloat(v.visitEvaluation.slaTarget);
      }).length;
      const slaMetPct = pct(slaMet, withSLA.length);
      const overdueCount = withSLA.length - slaMet;

      // Issue categories for aggregate chart
      const issueCategories = {};
      wVisits.forEach(v => {
        if (v.visitEvaluation?.issueOccurred === 'yes' && v.visitEvaluation?.issueCategory) {
          const cat = v.visitEvaluation.issueCategory;
          issueCategories[cat] = (issueCategories[cat] || 0) + 1;
        }
      });

      return {
        id: w.id, name: `${w.name} ${w.surname}`,
        totalVisits: total, homeVisits: homeV.length, siteVisits: siteV.length,
        totalKm: r1(totalKm), avgKmPerVisit, ecrfPct, sourcePct,
        deviationCount, capaOpenedCount,
        screenFailCount, dropPatientCount, slaMetPct, overdueCount, deviationPct,
        issueCategories,
      };
    }).filter(Boolean);

    if (stats.length === 0) return null;

    // ── Aggregate chart data ──
    const totalHomeV = s3Visits.filter(v => v.visitType === 'home');
    const totalSiteV = s3Visits.filter(v => v.visitType !== 'home');

    // 1. Home vs Site SLA Met %
    const slaCalc = arr => {
      const valid = arr.filter(v => v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate);
      const met = valid.filter(v => Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000) <= parseFloat(v.visitEvaluation.slaTarget)).length;
      return pct(met, valid.length);
    };
    const homeVsSiteSla = [
      { name: 'Ev', 'SLA Karşılanma %': slaCalc(totalHomeV) },
      { name: 'Hastane', 'SLA Karşılanma %': slaCalc(totalSiteV) },
    ];

    // 2. KM per Nurse (top 10)
    const kmPerNurse = [...stats].sort((a, b) => b.totalKm - a.totalKm).slice(0, 10).map(s => ({
      name: s.name.length > 18 ? s.name.substring(0, 17) + '…' : s.name,
      fullName: s.name, KM: s.totalKm,
    }));

    // 3. Top Issue Categories
    const allCats = {};
    stats.forEach(s => Object.entries(s.issueCategories).forEach(([c, n]) => { allCats[c] = (allCats[c] || 0) + n; }));
    const issueCategoryLabels = { iv_access: 'IV Erişim', patient_compliance: 'Hasta Uyumu', documentation: 'Dokümantasyon', logistics: 'Lojistik', protocol: 'Protokol', equipment: 'Ekipman', other: 'Diğer' };
    const topIssueCategories = Object.entries(allCats).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([c, n]) => ({ name: issueCategoryLabels[c] || c, Adet: n }));

    // 4. Issue Rate: Home vs Site
    const issueRateHomeVsSite = [
      { name: 'Ev', Oran: pct(totalHomeV.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length, totalHomeV.length) },
      { name: 'Hastane', Oran: pct(totalSiteV.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length, totalSiteV.length) },
    ];

    return { stats, homeVsSiteSla, kmPerNurse, topIssueCategories, issueRateHomeVsSite };
  }, [s3Visits, workers]);

  // ── Unified Operational Summary (story-driven) ────────────────────────
  const computeOpSummary = useCallback((visits) => {
    if (!visits || visits.length === 0) return null;

    const total = visits.length;
    const homeVisits = visits.filter(v => v.visitType === 'home');
    const siteVisits = visits.filter(v => v.visitType !== 'home');
    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;

    // Quality rate
    const issueCount = visits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length;
    const qualityRate = pct(total - issueCount, total);

    // SLA compliance (all visits)
    const withSLA = visits.filter(v =>
      v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate
    );
    const slaMet = withSLA.filter(v => {
      const cycle = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000);
      return cycle <= parseFloat(v.visitEvaluation.slaTarget);
    }).length;
    const slaRate = pct(slaMet, withSLA.length);

    // Documentation completeness
    const ecrfDone = visits.filter(v => v.visitEvaluation?.ecrfCompletionDate).length;
    const sourceDone = visits.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length;
    const signDone = visits.filter(v => v.visitEvaluation?.signatureCompletionDate).length;
    const docRate = Math.round((pct(ecrfDone, total) + pct(sourceDone, total) + pct(signDone, total)) / 3);

    // Safety & quality metrics
    const aeSaeCount = visits.filter(v => v.visitEvaluation?.wasAeSaeTaken === 'yes').length;
    const dcfCount = visits.filter(v => v.visitEvaluation?.dcfCreated === 'yes').length;
    const missedCount = visits.filter(v => v.visitEvaluation?.issueResultedInMiss === 'yes').length;
    const deviationCount = visits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
    const deviationRate = pct(deviationCount, total);
    const capaCount = visits.filter(v => v.visitEvaluation?.capaOpened === 'yes').length;
    const screenFail = siteVisits.filter(v => v.visitEvaluation?.screenFail === 'yes').length;
    const dropPatient = siteVisits.filter(v => v.visitEvaluation?.dropPatient === 'yes').length;
    const missingProcessEnd = visits.filter(v => !v.visitEvaluation?.processEndDate).length;

    // KM
    const totalKm = Math.round(visits.reduce((sum, v) =>
      sum + (parseFloat(v.visitEvaluation?.travelDistanceBefore) || 0), 0));

    // Avg cycle times
    const cycleCalc = (arr) => {
      const vals = arr.flatMap(v => {
        const vDate = v.date || v.firstVisitDate;
        if (!vDate || !v.visitEvaluation?.processEndDate) return [];
        const days = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(vDate)) / 86400000);
        return days >= 0 ? [days] : [];
      });
      return vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10) / 10 : null;
    };
    const avgCycleHome = cycleCalc(homeVisits);
    const avgCycleSite = cycleCalc(siteVisits);

    // Alerts
    const alerts = [];
    if (withSLA.length > 0 && slaRate < 80) alerts.push({ type: 'danger', metric: 'overdue', text: `SLA karşılanma oranı %${slaRate} — hedefin altında`, vars: { rate: slaRate } });
    if (pct(issueCount, total) > 10) alerts.push({ type: 'danger', metric: 'issues', text: `Sorun oranı %${pct(issueCount, total)} — yüksek seviyede`, vars: { rate: pct(issueCount, total) } });
    if (deviationRate > 5) alerts.push({ type: 'warning', metric: 'deviation', text: `Protokol sapma oranı %${deviationRate}`, vars: { rate: deviationRate } });
    if (missingProcessEnd > total * 0.2) alerts.push({ type: 'warning', metric: 'missingProcessEnd', text: `${missingProcessEnd} vizitte süreç bitiş tarihi eksik`, vars: { count: missingProcessEnd } });
    if (missedCount > 0) alerts.push({ type: 'danger', metric: 'missed', text: `${missedCount} vizit kaçırıldı`, vars: { count: missedCount } });
    if (aeSaeCount > 0) alerts.push({ type: 'info', metric: 'aesae', text: `${aeSaeCount} AE/SAE rapor edildi`, vars: { count: aeSaeCount } });

    // Monthly trend
    const monthlyData = {};
    visits.forEach(v => {
      const date = v.date || v.firstVisitDate || '';
      const month = date ? date.substring(0, 7) : null;
      if (!month) return;
      if (!monthlyData[month]) monthlyData[month] = { month, Ev: 0, Hastane: 0, Sorun: 0 };
      if (v.visitType === 'home') monthlyData[month].Ev++;
      else monthlyData[month].Hastane++;
      if (v.visitEvaluation?.issueOccurred === 'yes') monthlyData[month].Sorun++;
    });
    const monthlyTrend = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));

    // Ev vs Hastane comparison chart
    const compSlaCalc = (arr) => {
      const valid = arr.filter(v => v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate);
      const met = valid.filter(v => Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000) <= parseFloat(v.visitEvaluation.slaTarget)).length;
      return pct(met, valid.length);
    };
    const compDocCalc = (arr) => Math.round((pct(arr.filter(v => v.visitEvaluation?.ecrfCompletionDate).length, arr.length) + pct(arr.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length, arr.length)) / 2);

    const comparisonChart = [
      { name: 'Sorun Oranı (%)', Ev: pct(homeVisits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length, homeVisits.length), Hastane: pct(siteVisits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length, siteVisits.length) },
      { name: 'SLA Uyumu (%)', Ev: compSlaCalc(homeVisits), Hastane: compSlaCalc(siteVisits) },
      { name: 'Dokümantasyon (%)', Ev: compDocCalc(homeVisits), Hastane: compDocCalc(siteVisits) },
    ];

    // Quality KPI tiles
    const qualityTiles = [
      { label: 'Protokol Sapması', metric: 'deviation', count: deviationCount, rate: deviationRate, color: deviationCount > 0 ? '#c53030' : '#276749' },
      { label: 'CAPA Açılan', metric: 'capa', count: capaCount, rate: null, color: capaCount > 0 ? '#c05621' : '#276749' },
      { label: 'Screen Fail', metric: 'screenFail', count: screenFail, rate: pct(screenFail, siteVisits.length), color: screenFail > 0 ? '#c05621' : '#276749' },
      { label: 'Drop Patient', metric: 'dropPatient', count: dropPatient, rate: pct(dropPatient, siteVisits.length), color: dropPatient > 0 ? '#c05621' : '#276749' },
      { label: 'AE/SAE', metric: 'aesae', count: aeSaeCount, rate: null, color: aeSaeCount > 0 ? '#9b2c2c' : '#276749' },
      { label: 'DCF', metric: 'dcf', count: dcfCount, rate: null, color: dcfCount > 0 ? '#744210' : '#276749' },
    ];

    return {
      total, homeCount: homeVisits.length, siteCount: siteVisits.length,
      qualityRate, slaRate, docRate, totalKm,
      issueCount, missedCount, deviationCount, capaCount,
      screenFail, dropPatient, aeSaeCount, dcfCount,
      missingProcessEnd, overdueCount: withSLA.length - slaMet,
      avgCycleHome, avgCycleSite,
      alerts, monthlyTrend, comparisonChart, qualityTiles,
    };
  }, []);

  const section1Summary = useMemo(() => computeOpSummary(s1Visits), [s1Visits, computeOpSummary]);
  const section2Summary = useMemo(() => computeOpSummary(s2Visits), [s2Visits, computeOpSummary]);

  // Fetch all SMOs from Projects collection (not Centers)
  // This ensures that even if a center is deleted from Centers collection,
  // it will still appear in SMO Lead Reports as long as it exists in Projects
  useEffect(() => {
    const fetchAllSMOs = async () => {
      try {
        setLoadingSMOs(true);
        
        // Fetch all projects to get center information
        const projectsRef = collection(db, 'Projects');
        const projectsSnapshot = await getDocs(projectsRef);
        
        console.log('📦 Fetching SMOs from Projects collection');
        console.log('📦 Total projects found:', projectsSnapshot.docs.length);
        
        // First pass: Build a map of all centers and their SMO IDs from Centers collection
        const centersRef = collection(db, 'Centers');
        const centersSnapshot = await getDocs(centersRef);
        const centerToSmoMap = {}; // centerId -> smoId (PRIMER SMO)
        const centerDetailsFromCenters = {}; // centerId -> { name, city, district, status }
        
        centersSnapshot.docs.forEach(centerDoc => {
          const centerData = centerDoc.data();
          const centerId = centerDoc.id;
          const smoId = centerData.SMO;
          
          // Her merkez için bilgileri kaydet (SMO olsun olmasın)
          centerDetailsFromCenters[centerId] = {
            name: centerData.Adı || '',
            city: centerData.İl || '',
            district: centerData.İlçe || '',
            status: centerData.Durum || 'Aktif'
          };
          
          if (smoId) {
            centerToSmoMap[centerId] = smoId;
          }
        });
        
        console.log('📍 Total centers in Centers collection:', centersSnapshot.docs.length);
        console.log('📍 Centers with SMO assignment:', Object.keys(centerToSmoMap).length);
        console.log('📍 Centers without city field:', centersSnapshot.docs.filter(doc => !doc.data().İl).length);
        
        console.log('📍 Centers from Centers collection:', Object.keys(centerToSmoMap).length);
        
        // Initialize smoMap with all primer SMOs from Centers collection
        const smoMap = {}; // smoId -> { id, centers: [] }
        const processedCentersBySmo = {}; // smoId -> Set(centerId) to track which centers we've added to which SMO
        
        // Add all primer SMO assignments from Centers collection
        Object.entries(centerToSmoMap).forEach(([centerId, smoId]) => {
          if (!smoMap[smoId]) {
            smoMap[smoId] = { id: smoId, centers: [] };
            processedCentersBySmo[smoId] = new Set();
          }
          
          if (!processedCentersBySmo[smoId].has(centerId)) {
            const centerDetails = centerDetailsFromCenters[centerId];
            
            console.log(`✅ Primer SMO: ${smoId} <- Center: ${centerId} | Name: "${centerDetails?.name}" | City: "${centerDetails?.city}" | District: "${centerDetails?.district}"`);
            
            smoMap[smoId].centers.push({
              id: centerId,
              name: centerDetails.name,
              city: centerDetails.city,
              district: centerDetails.district,
              status: centerDetails.status,
              isDeleted: false,
              isPrimerSmo: true // From Centers collection = Primer
            });
            processedCentersBySmo[smoId].add(centerId);
          }
        });
        
        console.log('📍 Primer SMOs from Centers:', Object.keys(smoMap).length);

        // Add backup SMO assignments from Centers collection (SMOBackup field)
        centersSnapshot.docs.forEach(centerDoc => {
          const centerData = centerDoc.data();
          const centerId = centerDoc.id;
          const backupSmoId = centerData.SMOBackup;
          
          if (!backupSmoId) return; // No backup SMO for this center
          
          const primerSmoId = centerToSmoMap[centerId];
          
          // Skip if backup SMO is same as primer SMO
          if (backupSmoId === primerSmoId) {
            console.log(`⚠️ Skipping: Backup SMO same as primer for center ${centerId}`);
            return;
          }
          
          // Initialize backup SMO in map if not exists
          if (!smoMap[backupSmoId]) {
            smoMap[backupSmoId] = { id: backupSmoId, centers: [] };
            processedCentersBySmo[backupSmoId] = new Set();
          }
          
          // Add center to backup SMO if not already added
          if (!processedCentersBySmo[backupSmoId].has(centerId)) {
            const centerDetails = centerDetailsFromCenters[centerId];
            
            // If not in centerDetailsFromCenters map, get directly from centerData
            if (!centerDetails) {
              console.warn(`⚠️ Center ${centerId} not in centerDetailsFromCenters, reading directly from Centers document`);
            }
            
            const finalCenterDetails = centerDetails || {
              name: centerData.Adı || '',
              city: centerData.İl || '',
              district: centerData.İlçe || '',
              status: centerData.Durum || 'Aktif'
            };
            
            console.log(`✅ Backup SMO: ${backupSmoId} <- Center: ${centerId} | Name: "${finalCenterDetails.name}" | City: "${finalCenterDetails.city}" | District: "${finalCenterDetails.district}"`);
            
            smoMap[backupSmoId].centers.push({
              id: centerId,
              name: finalCenterDetails.name,
              city: finalCenterDetails.city,
              district: finalCenterDetails.district,
              status: finalCenterDetails.status,
              isDeleted: false,
              isPrimerSmo: false // Backup SMO
            });
            processedCentersBySmo[backupSmoId].add(centerId);
          }
        });
        
        console.log('📍 Backup SMOs added from Centers');

        // Process projects to handle deleted centers
        for (const projectDoc of projectsSnapshot.docs) {
          const projectData = projectDoc.data();
          const employees = projectData.employees || [];
          
          // Group employees by center in this project
          const centerEmployeesMap = {}; // centerId -> [employees]
          
          employees.forEach(emp => {
            const centerId = emp.centerId;
            if (!centerId) return;
            
            if (!centerEmployeesMap[centerId]) {
              centerEmployeesMap[centerId] = [];
            }
            centerEmployeesMap[centerId].push(emp);
          });
          
          // Process each center - only for deleted center recovery
          for (const [centerId, centerEmployees] of Object.entries(centerEmployeesMap)) {
            // Skip if center exists in Centers collection
            if (centerToSmoMap[centerId]) continue;

            const centerName = centerEmployees[0]?.centerName || 'Bilinmeyen Merkez';

            // Try to get center details from Centers collection even if no SMO assigned
            let centerDetails = {
              name: centerName,
              city: '',
              district: '',
              status: 'Aktif'
            };

            // Check if center exists in Centers collection (but maybe without SMO)
            const centerDoc = centersSnapshot.docs.find(doc => doc.id === centerId);
            if (centerDoc) {
              const centerData = centerDoc.data();
              centerDetails = {
                name: centerData.Adı || centerName,
                city: centerData.İl || '',
                district: centerData.İlçe || '',
                status: centerData.Durum || 'Aktif'
              };
            } else {
              // Center not in Centers collection - try to extract city from centerName
              // Common patterns: "İzmir Şehir Hastanesi", "Ege Üniversitesi Tıp Fakültesi Hastanesi"
              const cityPatterns = {
                'İstanbul': ['İstanbul', 'Cerrahpaşa', 'Şişli', 'Kartal', 'Haydarpaşa', 'Bağcılar', 'Bakırköy', 'Beşiktaş', 'Fatih', 'Üsküdar', 'Kadıköy', 'Pendik', 'Ümraniye'],
                'Ankara': ['Ankara', 'Hacettepe', 'Gazi Üniversitesi'],
                'İzmir': ['İzmir', 'Ege Üniversitesi', 'Dokuz Eylül', '9 Eylül', 'Katip Çelebi', 'Tepecik', 'Bornova', 'Karşıyaka', 'Buca', 'Çiğli', 'Bayraklı', 'Balçova'],
                'Bursa': ['Bursa', 'Uludağ Üniversitesi'],
                'Antalya': ['Antalya', 'Akdeniz Üniversitesi'],
                'Adana': ['Adana', 'Çukurova Üniversitesi'],
                'Konya': ['Konya', 'Selçuk Üniversitesi', 'Necmettin Erbakan'],
                'Mersin': ['Mersin'],
                'Gaziantep': ['Gaziantep'],
                'Kocaeli': ['Kocaeli', 'İzmit'],
                'Diyarbakır': ['Diyarbakır', 'Dicle Üniversitesi'],
                'Kayseri': ['Kayseri', 'Erciyes Üniversitesi'],
                'Eskişehir': ['Eskişehir', 'Osmangazi Üniversitesi'],
                'Samsun': ['Samsun', 'Ondokuz Mayıs Üniversitesi'],
                'Trabzon': ['Trabzon', 'Karadeniz Teknik Üniversitesi']
              };
              
              let extractedCity = '';
              for (const [city, patterns] of Object.entries(cityPatterns)) {
                for (const pattern of patterns) {
                  if (centerName.includes(pattern)) {
                    extractedCity = city;
                    break;
                  }
                }
                if (extractedCity) break;
              }
              
              centerDetails = {
                name: centerName,
                city: extractedCity,
                district: '',
                status: 'Aktif'
              };
              
              if (extractedCity) {
                console.log(`📍 Extracted city "${extractedCity}" from center name: ${centerName}`);
              } else {
                console.warn(`⚠️ Could not extract city from center name: ${centerName}`);
              }
            }
            
            const centerInfo = {
              id: centerId,
              name: centerDetails.name,
              city: centerDetails.city,
              district: centerDetails.district,
              status: centerDetails.status,
              isDeleted: !centerDoc // Mark as deleted only if not in Centers collection at all
            };

            // If center is deleted, try to recover the SMO from employee records
            const empWithSmo = centerEmployees.find(emp => emp.smoId);
            if (empWithSmo) {
              const recoveredSmoId = empWithSmo.smoId;
              
              if (!smoMap[recoveredSmoId]) {
                smoMap[recoveredSmoId] = {
                  id: recoveredSmoId,
                  centers: []
                };
                processedCentersBySmo[recoveredSmoId] = new Set();
              }
              
              if (!processedCentersBySmo[recoveredSmoId].has(centerId)) {
                console.log(`🔄 Recovering deleted center: ${centerId} for SMO ${recoveredSmoId}`);
                smoMap[recoveredSmoId].centers.push({
                  ...centerInfo,
                  isPrimerSmo: true // Assume primer for recovered SMOs
                });
                processedCentersBySmo[recoveredSmoId].add(centerId);
              }
            }
          }
        }
        
        console.log('📊 SMO Map created:', Object.keys(smoMap).length, 'SMOs found');
        
        // Debug: Log SMO centers with primer/backup info
        Object.entries(smoMap).forEach(([smoId, smoData]) => {
          const primerCenters = smoData.centers.filter(c => c.isPrimerSmo);
          const backupCenters = smoData.centers.filter(c => !c.isPrimerSmo);
          if (backupCenters.length > 0) {
            console.log(`  SMO ${smoId}: ${primerCenters.length} primer, ${backupCenters.length} backup centers`);
          }
        });
        
        // Fetch employee details for each SMO in parallel
        const smoIds = Object.keys(smoMap);
        const empDocs = await Promise.all(
          smoIds.map(smoId => getDoc(doc(db, 'Employees', smoId)).catch(() => null))
        );
        const smoListData = smoIds.map((smoId, idx) => {
          const empDoc = empDocs[idx];
          if (!empDoc || !empDoc.exists()) return null;
          const empData = empDoc.data();
          return {
            id: smoId,
            name: empData.eName || '',
            surname: empData.eSurname || '',
            email: empData.eMail || '',
            phone: empData.phone || '',
            centers: smoMap[smoId].centers,
            centerCount: smoMap[smoId].centers.length
          };
        }).filter(Boolean);
        
        // Sort by name
        smoListData.sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));

        setSmoList(smoListData);
        setLoadingSMOs(false);
      } catch (error) {
        console.error('Error fetching SMOs:', error);
        toast.error(labelRef.current('toast.smoListError'));
        setLoadingSMOs(false);
      }
    };

    fetchAllSMOs();
  }, []);

  // Find projects where the current user is assigned as a "backup" SMO Lead.
  // For each such project, build a delegated view that mirrors the primer
  // SMO Lead's data, scoped to that single project.
  useEffect(() => {
    if (isAdmin || !employeeId) {
      setDelegatedSmoViews([]);
      return;
    }
    const fetchBackupDelegations = async () => {
      try {
        const [projectsSnap, centersSnap] = await Promise.all([
          getDocs(collection(db, 'Projects')),
          getDocs(collection(db, 'Centers')),
        ]);

        // primerSmoId -> [center objects]
        const primerCentersMap = {};
        centersSnap.docs.forEach((cd) => {
          const c = cd.data();
          if (!c.SMO) return;
          if (!primerCentersMap[c.SMO]) primerCentersMap[c.SMO] = [];
          primerCentersMap[c.SMO].push({
            id: cd.id,
            name: c['Adı'] || '',
            city: c['İl'] || '',
            district: c['İlçe'] || '',
            status: c['Durum'] || 'Aktif',
          });
        });

        // Find delegations: (primerSmoId, projectId)
        const delegations = [];
        const primerIdsToFetch = new Set();
        const projectsById = {};

        projectsSnap.docs.forEach((pd) => {
          const data = pd.data();
          projectsById[pd.id] = data;
          const employees = data.employees || [];
          const isBackupSmoLeadHere = employees.some(
            (e) =>
              e.id === employeeId &&
              String(e.role || '').toLowerCase() === 'smo lead' &&
              String(e.workerType || '').toLowerCase() === 'backup'
          );
          if (!isBackupSmoLeadHere) return;

          const primers = employees.filter(
            (e) =>
              String(e.role || '').toLowerCase() === 'smo lead' &&
              String(e.workerType || '').toLowerCase() === 'primer' &&
              e.id && e.id !== employeeId
          );
          if (primers.length === 0) return;

          // Dedupe primers within the project
          const seen = new Set();
          primers.forEach((p) => {
            if (seen.has(p.id)) return;
            seen.add(p.id);
            delegations.push({
              primerId: p.id,
              projectId: pd.id,
              projectTitle: data.titleShort || data.titleLong || '',
            });
            primerIdsToFetch.add(p.id);
          });
        });

        if (delegations.length === 0) {
          setDelegatedSmoViews([]);
          return;
        }

        const primerIds = Array.from(primerIdsToFetch);
        const primerDocs = await Promise.all(
          primerIds.map((id) => getDoc(doc(db, 'Employees', id)).catch(() => null))
        );
        const primerDataMap = {};
        primerIds.forEach((id, idx) => {
          const d = primerDocs[idx];
          if (d && d.exists()) primerDataMap[id] = d.data();
        });

        const views = delegations
          .map((d) => {
            const primerData = primerDataMap[d.primerId];
            if (!primerData) return null;
            const allPrimerCenters = primerCentersMap[d.primerId] || [];
            const projectData = projectsById[d.projectId] || {};
            const projEmployees = projectData.employees || [];
            const centerIdsInProject = new Set(
              projEmployees.map((e) => e.centerId).filter(Boolean)
            );
            // Scope primer's centers to those actually used in this project
            const scoped = allPrimerCenters.filter((c) =>
              centerIdsInProject.has(c.id)
            );
            const centers = scoped.length > 0 ? scoped : allPrimerCenters;
            return {
              id: d.primerId,
              viewKey: `${d.primerId}__${d.projectId}`,
              name: primerData.eName || '',
              surname: primerData.eSurname || '',
              email: primerData.eMail || '',
              phone: primerData.phone || '',
              centers,
              centerCount: centers.length,
              restrictToProjectId: d.projectId,
              restrictToProjectTitle: d.projectTitle,
              delegatedFromBackup: true,
            };
          })
          .filter(Boolean);

        views.sort((a, b) => {
          const an = `${a.name} ${a.surname}`;
          const bn = `${b.name} ${b.surname}`;
          const c = an.localeCompare(bn, 'tr');
          if (c !== 0) return c;
          return (a.restrictToProjectTitle || '').localeCompare(
            b.restrictToProjectTitle || '',
            'tr'
          );
        });

        setDelegatedSmoViews(views);
      } catch (err) {
        console.error('Error loading backup SMO delegations:', err);
      }
    };
    fetchBackupDelegations();
  }, [isAdmin, employeeId]);

  // Find centers where the current user is set as the "Yedek SMO" (backup).
  // Group them by the center's primer SMO and build a delegated view per SMO,
  // mirroring that SMO Lead's reports but scoped to the backup-assigned centers.
  useEffect(() => {
    if (isAdmin || !employeeId) {
      setCenterBackupViews([]);
      return;
    }
    const fetchCenterBackupDelegations = async () => {
      try {
        const centersSnap = await getDocs(collection(db, 'Centers'));

        // primerSmoId -> [center objects] for centers where this user is backup
        const backupCentersByPrimer = {};
        centersSnap.docs.forEach((cd) => {
          const c = cd.data();
          if (c.SMOBackup !== employeeId) return;
          const primerSmoId = c.SMO;
          if (!primerSmoId || primerSmoId === employeeId) return; // need a distinct primer
          if (!backupCentersByPrimer[primerSmoId]) backupCentersByPrimer[primerSmoId] = [];
          backupCentersByPrimer[primerSmoId].push({
            id: cd.id,
            name: c['Adı'] || '',
            city: c['İl'] || '',
            district: c['İlçe'] || '',
            status: c['Durum'] || 'Aktif',
          });
        });

        const primerIds = Object.keys(backupCentersByPrimer);
        if (primerIds.length === 0) {
          setCenterBackupViews([]);
          return;
        }

        const primerDocs = await Promise.all(
          primerIds.map((id) => getDoc(doc(db, 'Employees', id)).catch(() => null))
        );

        const views = primerIds
          .map((primerId, idx) => {
            const d = primerDocs[idx];
            if (!d || !d.exists()) return null;
            const primerData = d.data();
            const centers = backupCentersByPrimer[primerId];
            return {
              id: primerId,
              viewKey: `centerbackup__${primerId}`,
              name: primerData.eName || '',
              surname: primerData.eSurname || '',
              email: primerData.eMail || '',
              phone: primerData.phone || '',
              centers,
              centerCount: centers.length,
              delegatedFromCenterBackup: true,
            };
          })
          .filter(Boolean);

        views.sort((a, b) =>
          `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr')
        );

        setCenterBackupViews(views);
      } catch (err) {
        console.error('Error loading center backup delegations:', err);
      }
    };
    fetchCenterBackupDelegations();
  }, [isAdmin, employeeId]);

  // Auto-select own SMO for non-admin SMO Lead users, or a delegated view
  // when the user is only a backup SMO Lead.
  useEffect(() => {
    if (isAdmin || selectedSMO || !employeeId) return;
    const ownSmo = smoList.find((s) => s.id === employeeId);
    if (ownSmo) {
      setSelectedSMO({ ...ownSmo, viewKey: ownSmo.id });
      return;
    }
    const allDelegated = [...delegatedSmoViews, ...centerBackupViews];
    if (allDelegated.length === 1) {
      setSelectedSMO(allDelegated[0]);
    }
  }, [smoList, delegatedSmoViews, centerBackupViews, isAdmin, employeeId, selectedSMO]);

  // Fetch workers when an SMO is selected
  const fetchWorkersForSMO = useCallback(async () => {
    if (!selectedSMO) {
      setWorkers([]);
      setSmoCenters([]);
      return;
    }

    setLoadingWorkers(true);
    setExpandedWorkers({});
    
    try {
      // Get all center IDs for this SMO
      const centerIds = selectedSMO.centers.map(c => c.id);
      setSmoCenters(selectedSMO.centers);
      
      console.log('🏥 SMO Centers Data:', selectedSMO.centers.map(c => ({
        id: c.id,
        name: c.name,
        city: c.city,
        district: c.district,
        isPrimer: c.isPrimerSmo
      })));
      
      console.log('🔄 Fetching workers for SMO:', selectedSMO.name, selectedSMO.surname);
      console.log('📍 Center IDs:', centerIds);
      
      // Fetch all projects and find employees assigned to these centers
      const projectsRef = collection(db, 'Projects');
      const projectsSnapshot = await getDocs(projectsRef);
      
      console.log('📦 Total projects found:', projectsSnapshot.docs.length);
      
      // First pass: collect all center employees and unique employee IDs
      const empProjectMap = {}; // empId -> { emp, projects: [] }
      
      for (const projectDoc of projectsSnapshot.docs) {
        // For backup-SMO-Lead delegated views: only include the one project
        // this user was granted access to.
        if (selectedSMO.restrictToProjectId && projectDoc.id !== selectedSMO.restrictToProjectId) {
          continue;
        }
        const projectData = projectDoc.data();
        const projectEmployees = projectData.employees || [];

        const centerEmployees = projectEmployees.filter(emp =>
          centerIds.includes(emp.centerId)
        );
        
        if (centerEmployees.length > 0) {
          console.log(`📋 Project: ${projectData.titleShort || projectData.titleLong}`, {
            totalEmployees: projectEmployees.length,
            centerEmployees: centerEmployees.length
          });
        }
        
        for (const emp of centerEmployees) {
          // workerType: 'primer' veya 'backup' (string)
          // blindStatus: 'blinded' veya 'unblinded' (string)
          // Güvenli dönüşüm - hem string hem de undefined/null durumlarını kontrol et
          const isPrimer = emp.workerType === 'primer' || emp.workerType === 'Primer';
          const isBlinded = emp.blindStatus === 'blinded' || emp.blindStatus === 'Blinded';
          
          const projectInfo = {
            projectId: projectDoc.id,
            // String(): sayısal kaydedilmiş proje kodları arama/sıralamada
            // metin işlemlerine girdiği için tipi burada sabitliyoruz.
            projectTitle: String(projectData.titleShort || projectData.titleLong || ''),
            role: emp.role || '',
            rate: emp.rate,
            centerId: emp.centerId,
            centerName: emp.centerName || selectedSMO.centers.find(c => c.id === emp.centerId)?.name || '',
            assignedAt: emp.assignedAt,
            status: emp.status || 'active',
            // Merkezden ayrılmış atamalar rapordan düşmez, rozetle işaretlenir.
            isArchived: isArchivedAssignment(emp),
            unassignedAt: emp.unassignedAt || '',
            isPrimer: isPrimer, // Primer/Backup bilgisi
            isBlinded: isBlinded, // Blinded/Unblinded bilgisi
            isFinished: projectData.isFinished === true // Proje bitti mi
          };

          console.log(`👤 Employee: ${emp.name} ${emp.surname} | Project: ${projectInfo.projectTitle}`, {
            rawWorkerType: emp.workerType,
            rawBlindStatus: emp.blindStatus,
            convertedIsPrimer: projectInfo.isPrimer,
            convertedIsBlinded: projectInfo.isBlinded,
            centerId: emp.centerId,
            centerName: projectInfo.centerName
          });

          if (empProjectMap[emp.id]) {
            empProjectMap[emp.id].projects.push(projectInfo);
          } else {
            empProjectMap[emp.id] = { emp, projects: [projectInfo] };
          }
        }
      }

      // Fetch all unique employee details in parallel
      const uniqueEmpIds = Object.keys(empProjectMap);
      console.log('👥 Unique employees found:', uniqueEmpIds.length);
      
      const empDocs = await Promise.all(
        uniqueEmpIds.map(id => getDoc(doc(db, 'Employees', id)).catch(() => null))
      );
      const empDataMap = {};
      uniqueEmpIds.forEach((id, idx) => {
        const empDoc = empDocs[idx];
        empDataMap[id] = empDoc && empDoc.exists() ? empDoc.data() : null;
      });

      // Build workersData
      let workersData = uniqueEmpIds.map(id => {
        const { emp, projects } = empProjectMap[id];
        const employeeData = empDataMap[id];
        return {
          id,
          name: emp.name || '',
          surname: emp.surname || '',
          email: employeeData?.eMail || '',
          phone: employeeData?.phone || '',
          address: employeeData?.address || '',
          projects
        };
      });

      // Sort workers by name
      workersData.sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));
      
      console.log('✅ Workers data ready:', workersData.length);
      
      setWorkers(workersData);
      setLoadingWorkers(false);
      toast.success(labelRef.current('toast.workersUpdated'));
    } catch (error) {
      console.error('❌ Error fetching workers:', error);
      toast.error(labelRef.current('toast.workersError'));
      setLoadingWorkers(false);
    }
  }, [selectedSMO]);

  useEffect(() => {
    fetchWorkersForSMO();
  }, [fetchWorkersForSMO]);

  // Ekipteki çalışanların eksik zorunlu CV/GCP/IATA belgeleri. Liste her
  // yenilendiğinde tek seferde (parçalı sorgu ile) çekilir; satırdaki belge
  // butonu ve tablo üstündeki özet uyarı bu veriyi kullanır.
  useEffect(() => {
    if (workers.length === 0) {
      setMissingDocsByWorker({});
      return;
    }

    let cancelled = false;
    const fetchMissingDocs = async () => {
      try {
        const result = await fetchMissingRequiredDocumentsByEmployee(workers.map((w) => w.id));
        if (!cancelled) setMissingDocsByWorker(result);
      } catch (error) {
        // Uyarı ikincil bilgi: hata durumunda sayfa belgesiz çalışmaya devam eder
        console.error('Eksik belge bilgisi alınamadı:', error);
        if (!cancelled) setMissingDocsByWorker({});
      }
    };

    fetchMissingDocs();
    return () => {
      cancelled = true;
    };
  }, [workers]);

  // Fetch visits for workers under selected SMO (batched queries)
  useEffect(() => {
    if (workers.length === 0) { setAllVisits([]); return; }
    const fetchVisits = async () => {
      setLoadingVisits(true);
      try {
        const workerIds = workers.map(w => w.id);
        // Firestore 'in' supports max 30 values — batch into chunks
        const BATCH_SIZE = 30;
        const batches = [];
        for (let i = 0; i < workerIds.length; i += BATCH_SIZE) {
          batches.push(workerIds.slice(i, i + BATCH_SIZE));
        }
        const results = await Promise.all(
          batches.map(batch => {
            const q = query(collection(db, 'Visits'), where('employeeId', 'in', batch));
            return getDocs(q);
          })
        );
        let visits = results.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
        // Restrict to the delegated project for backup-SMO-Lead views
        if (selectedSMO?.restrictToProjectId) {
          visits = visits.filter(v => v.projectId === selectedSMO.restrictToProjectId);
        }
        setAllVisits(visits);
      } catch (e) {
        console.error('Error fetching visits for dashboard:', e);
      } finally {
        setLoadingVisits(false);
      }
    };
    fetchVisits();
  }, [workers, selectedSMO]);

  // Fetch calendar jobs for workers under selected SMO (batched queries)
  useEffect(() => {
    if (workers.length === 0) { setAllCalendarJobs([]); return; }
    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        const workerIds = workers.map(w => w.id);
        // Firestore 'in' supports max 30 values — batch into chunks
        const BATCH_SIZE = 30;
        const batches = [];
        for (let i = 0; i < workerIds.length; i += BATCH_SIZE) {
          batches.push(workerIds.slice(i, i + BATCH_SIZE));
        }
        const results = await Promise.all(
          batches.map(batch => {
            const q = query(collection(db, 'CalendarJobs'), where('employeeId', 'in', batch));
            return getDocs(q);
          })
        );
        let jobs = results.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
        if (selectedSMO?.restrictToProjectId) {
          jobs = jobs.filter(j => j.projectId === selectedSMO.restrictToProjectId);
        }
        setAllCalendarJobs(jobs);
      } catch (e) {
        console.error('Error fetching calendar jobs for dashboard:', e);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, [workers, selectedSMO]);

  // The legacy `dark-mode` body class is managed by ThemeContext

  // Scroll handlers
  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [workers]);

  useEffect(() => {
    const checkScroll = () => {
      if (tableContainerRef.current) {
        const { scrollWidth, clientWidth } = tableContainerRef.current;
        setShowRightScroll(scrollWidth > clientWidth);
      }
    };
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [workers]);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Handle SMO card click
  const handleSMOClick = (smo) => {
    const smoKey = smo.viewKey || smo.id;
    const currentKey = selectedSMO?.viewKey || selectedSMO?.id;
    if (currentKey === smoKey) {
      setSelectedSMO(null); // Deselect if already selected
      setCollapsedCities({}); // Reset collapsed cities when deselecting
    } else {
      setSelectedSMO({ ...smo, viewKey: smoKey });
      setCollapsedCities({}); // Reset collapsed cities for new SMO selection
    }
  };

  // Toggle expanded worker row
  const toggleWorkerExpand = (workerId) => {
    setExpandedWorkers(prev => ({
      ...prev,
      [workerId]: !prev[workerId]
    }));
  };

  // Open visits modal for a specific employee + project
  const handleViewVisits = useCallback((worker, project) => {
    // Use already-loaded allVisits instead of making a fresh Firestore query
    const workerVisits = allVisits.filter(
      v => v.employeeId === worker.id && v.projectId === project.projectId
    );

    if (workerVisits.length === 0) {
      toast.info(labelRef.current('toast.noVisits'));
      return;
    }

    setSelectedEmployee({
      id: worker.id,
      name: `${worker.name} ${worker.surname}`,
      project: {
        id: project.projectId,
        title: project.projectTitle
      },
      visits: workerVisits
    });
    setShowVisitsModal(true);
  }, [allVisits]);

  // Filter workers by search term and status
  const filteredWorkers = useMemo(() => {
    return workers.map(worker => {
      // Filter projects based on all criteria
      let filteredProjects = worker.projects;

      // Status filter
      if (statusFilter === 'active') {
        filteredProjects = filteredProjects.filter(p => p.status === 'active');
      } else if (statusFilter === 'inactive') {
        filteredProjects = filteredProjects.filter(p => p.status !== 'active');
      } else if (statusFilter === 'incomplete') {
        // For incomplete filter, keep all projects but we'll check tasks later
        filteredProjects = filteredProjects;
      }

      // Primer/Backup filter
      if (primerFilter === 'primer') {
        filteredProjects = filteredProjects.filter(p => p.isPrimer === true);
      } else if (primerFilter === 'backup') {
        filteredProjects = filteredProjects.filter(p => p.isPrimer === false);
      }

      // Blinded/Unblinded filter
      if (blindedFilter === 'blinded') {
        filteredProjects = filteredProjects.filter(p => p.isBlinded === true);
      } else if (blindedFilter === 'unblinded') {
        filteredProjects = filteredProjects.filter(p => p.isBlinded === false);
      }

      // Hide finished projects
      if (hideFinished) {
        filteredProjects = filteredProjects.filter(p => !p.isFinished);
      }

      // Search filter
      if (searchTerm) {
        // Alanlar her zaman string değil (sayısal proje kodu, telefon vb.), bu
        // yüzden dönüşüm toSearchText üzerinden yapılır.
        const matchesWorker = matchesSearchTerm(
          searchTerm,
          `${worker.name} ${worker.surname}`,
          worker.email,
          worker.phone
        );

        if (!matchesWorker) {
          // If worker doesn't match, filter projects by search term
          filteredProjects = filteredProjects.filter(p =>
            matchesSearchTerm(searchTerm, p.projectTitle, p.centerName)
          );
        }
      }

      // Incomplete tasks filter
      if (statusFilter === 'incomplete') {
        const hasIncompleteTasks = incompleteTasks.byWorker[worker.id] && incompleteTasks.byWorker[worker.id].length > 0;
        if (!hasIncompleteTasks) {
          return null; // Exclude worker if no incomplete tasks
        }
      }

      // Return worker with filtered projects, or null if no projects match
      if (filteredProjects.length === 0) {
        return null;
      }

      return {
        ...worker,
        projects: filteredProjects
      };
    }).filter(Boolean); // Remove null entries
  }, [workers, searchTerm, statusFilter, primerFilter, blindedFilter, hideFinished, incompleteTasks]);

  // Download Excel
  const downloadExcel = () => {
    const dataToExport = filteredWorkers.flatMap(worker => 
      worker.projects.map(project => ({
        'SMO': selectedSMO ? `${selectedSMO.name} ${selectedSMO.surname}` : '',
        'Çalışan Adı': `${worker.name} ${worker.surname}`,
        'E-posta': worker.email || '-',
        'Telefon': worker.phone || '-',
        'Merkez': project.centerName || '-',
        'Merkezden Ayrılma': project.isArchived ? formatUnassignedDate(project) : '',
        'Proje': project.projectTitle,
        'Rol': project.role,
        'Durum': project.status === 'active' ? 'Aktif' : 'Pasif',
        'Tip': project.isPrimer ? 'Primer' : 'Backup',
        'Kör Durumu': project.isBlinded ? 'Blinded' : 'Unblinded',
        'Atanma Tarihi': project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') || 
                        (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '-')
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');
    
    const smoName = selectedSMO ? `${selectedSMO.name}_${selectedSMO.surname}` : 'Tüm_SMO';
    XLSX.writeFile(workbook, `${smoName}_Çalışan_Listesi.xlsx`);
    
    toast.success(labelRef.current('toast.excelDownloaded'));
  };

  const downloadCapaExcel = () => {
    if (!section1Summary) return;
    const smoName = selectedSMO ? `${selectedSMO.name}_${selectedSMO.surname}` : 'SMO';
    const wb = XLSX.utils.book_new();

    // Sheet 1: Operational Summary
    const summaryData = [
      { 'Metrik': 'Toplam Vizit', 'Değer': section1Summary.total },
      { 'Metrik': 'Ev Vizitleri', 'Değer': section1Summary.homeCount },
      { 'Metrik': 'Hastane Vizitleri', 'Değer': section1Summary.siteCount },
      { 'Metrik': 'Sorunsuz Vizit Oranı (%)', 'Değer': section1Summary.qualityRate },
      { 'Metrik': 'SLA Karşılanma Oranı (%)', 'Değer': section1Summary.slaRate },
      { 'Metrik': 'Dokümantasyon Tamlığı (%)', 'Değer': section1Summary.docRate },
      { 'Metrik': 'Toplam KM', 'Değer': section1Summary.totalKm },
      { 'Metrik': 'Ort. Döngü Süresi - Ev (gün)', 'Değer': section1Summary.avgCycleHome ?? '-' },
      { 'Metrik': 'Ort. Döngü Süresi - Hastane (gün)', 'Değer': section1Summary.avgCycleSite ?? '-' },
      { 'Metrik': 'Sorunlu Vizit', 'Değer': section1Summary.issueCount },
      { 'Metrik': 'Kaçırılan Vizit', 'Değer': section1Summary.missedCount },
      { 'Metrik': 'Protokol Sapması', 'Değer': section1Summary.deviationCount },
      { 'Metrik': 'CAPA Açılan', 'Değer': section1Summary.capaCount },
      { 'Metrik': 'Screen Fail', 'Değer': section1Summary.screenFail },
      { 'Metrik': 'Drop Patient', 'Değer': section1Summary.dropPatient },
      { 'Metrik': 'AE/SAE', 'Değer': section1Summary.aeSaeCount },
      { 'Metrik': 'DCF', 'Değer': section1Summary.dcfCount },
      { 'Metrik': 'Gecikmiş Vizit', 'Değer': section1Summary.overdueCount },
      { 'Metrik': 'Süreç Bitiş Tarihi Eksik', 'Değer': section1Summary.missingProcessEnd },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Genel Özet');

    // Sheet 2: Coordinator Breakdown
    if (capaDashboard) {
      const coordData = capaDashboard.coordinators.map(c => ({
        'Koordinatör': c.name,
        'Ev Viziti': c.totalVisits,
        'Toplam Sorun': c.totalIssues,
        'Kaçırılan Vizit': c.missedVisits,
        'AE/SAE Alınan': c.aeSaeCount,
        'DCF Oluşturulan': c.dcfCreatedCount,
        'Sorun Oranı (%)': c.totalVisits > 0 ? Math.round(c.totalIssues / c.totalVisits * 100) : 0,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(coordData), 'Koordinatör Dağılımı');
    }

    XLSX.writeFile(wb, `${smoName}_Operasyonel_Ozet.xlsx`);
    toast.success(labelRef.current('toast.overviewExcel'));
  };

  const downloadHastaneKpiExcel = () => {
    if (!section2Summary) return;
    const smoName = selectedSMO ? `${selectedSMO.name}_${selectedSMO.surname}` : 'SMO';
    const data = [
      { 'Metrik': 'Toplam Vizit', 'Değer': section2Summary.total },
      { 'Metrik': 'Hastane Viziti', 'Değer': section2Summary.siteCount },
      { 'Metrik': 'Ev Viziti', 'Değer': section2Summary.homeCount },
      { 'Metrik': 'Protokol Sapması', 'Değer': section2Summary.deviationCount },
      { 'Metrik': 'CAPA Açılan', 'Değer': section2Summary.capaCount },
      { 'Metrik': 'Screen Fail', 'Değer': section2Summary.screenFail },
      { 'Metrik': 'Drop Patient', 'Değer': section2Summary.dropPatient },
      { 'Metrik': 'AE/SAE', 'Değer': section2Summary.aeSaeCount },
      { 'Metrik': 'DCF', 'Değer': section2Summary.dcfCount },
      { 'Metrik': 'Gecikmiş Vizit', 'Değer': section2Summary.overdueCount },
      { 'Metrik': 'Kaçırılan Vizit', 'Değer': section2Summary.missedCount },
      { 'Metrik': 'Süreç Bitiş Tarihi Eksik', 'Değer': section2Summary.missingProcessEnd },
      { 'Metrik': 'Hasta Çıkış Oranı (%)', 'Değer': section2Summary.siteCount > 0 ? Math.round(section2Summary.dropPatient / section2Summary.siteCount * 100) : 0 },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kalite & Risk');
    XLSX.writeFile(wb, `${smoName}_Kalite_Risk.xlsx`);
    toast.success(labelRef.current('toast.qualityExcel'));
  };

  const downloadEmployeeStatsExcel = () => {
    if (!employeeStats) return;
    const smoName = selectedSMO ? `${selectedSMO.name}_${selectedSMO.surname}` : 'SMO';
    const data = employeeStats.stats.map(emp => ({
      'Saha Koordinatörü': emp.name,
      'Toplam Vizit': emp.totalVisits,
      'Ev Vizitleri': emp.homeVisits,
      'Hastane Vizitleri': emp.siteVisits,
      'Toplam KM': emp.totalKm,
      'Ort. KM/Vizit': emp.avgKmPerVisit,
      'eCRF %': emp.ecrfPct,
      'Kaynak %': emp.sourcePct,
      'Sapma Sayısı': emp.deviationCount,
      'CAPA Açılan Sayısı': emp.capaOpenedCount,
      'Tarama Başarısız Sayısı': emp.screenFailCount,
      'Hasta Çıkış Sayısı': emp.dropPatientCount,
      'SLA Karşılanma %': emp.slaMetPct,
      'Gecikme Sayısı': emp.overdueCount,
      'Sapma %': emp.deviationPct,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Çalışan Performans');
    XLSX.writeFile(wb, `${smoName}_Calisan_Performans.xlsx`);
    toast.success(labelRef.current('toast.performanceExcel'));
  };

  // ── Admin-only management: remove a worker from this SMO, or remove the SMO ──
  // confirmAction: { type: 'worker', worker } | { type: 'smo' } | null
  const [confirmAction, setConfirmAction] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Remove an employee from the selected SMO by deleting their assignment
  // entries for this SMO's centers from both the Projects and Employees docs.
  const removeWorkerFromSMO = useCallback(async (worker) => {
    if (!selectedSMO || !worker) return;
    const centerIds = selectedSMO.centers.map(c => c.id);
    setRemoving(true);
    try {
      const projectsSnap = await getDocs(collection(db, 'Projects'));
      const updates = [];

      projectsSnap.docs.forEach(pd => {
        // Respect project-scoped (delegated) views, though admins won't have one
        if (selectedSMO.restrictToProjectId && pd.id !== selectedSMO.restrictToProjectId) return;
        const data = pd.data();
        const emps = data.employees || [];
        const filtered = emps.filter(
          e => !(e.id === worker.id && centerIds.includes(e.centerId))
        );
        if (filtered.length !== emps.length) {
          updates.push(updateDoc(doc(db, 'Projects', pd.id), { employees: filtered }));
        }
      });

      // Mirror the removal in the employee's own projects array
      const empRef = doc(db, 'Employees', worker.id);
      const empDoc = await getDoc(empRef);
      if (empDoc.exists()) {
        const projects = empDoc.data().projects || [];
        const filteredProjects = projects.filter(p => {
          if (selectedSMO.restrictToProjectId && p.id !== selectedSMO.restrictToProjectId) return true;
          return !centerIds.includes(p.centerId);
        });
        if (filteredProjects.length !== projects.length) {
          updates.push(updateDoc(empRef, { projects: filteredProjects }));
        }
      }

      await Promise.all(updates);
      toast.success(labelRef.current('toast.workerRemoved', { name: `${worker.name} ${worker.surname}` }));
      setConfirmAction(null);
      await fetchWorkersForSMO();
    } catch (error) {
      console.error('Error removing worker from SMO:', error);
      toast.error(labelRef.current('toast.workerRemoveError'));
    } finally {
      setRemoving(false);
    }
  }, [selectedSMO, fetchWorkersForSMO]);

  // Remove the SMO designation by clearing the SMO field on all of its centers.
  // The centers themselves and their project assignments are untouched.
  const removeSMO = useCallback(async () => {
    if (!selectedSMO) return;
    const centerIds = selectedSMO.centers.map(c => c.id);
    setRemoving(true);
    try {
      await Promise.all(
        centerIds.map(id => updateDoc(doc(db, 'Centers', id), { SMO: '' }))
      );
      toast.success(labelRef.current('toast.smoRemoved', { name: `${selectedSMO.name} ${selectedSMO.surname}`, count: centerIds.length }));
      // Drop the removed SMO from the list and clear selection (no full reload)
      const removedId = selectedSMO.id;
      setSmoList(prev => prev.filter(s => s.id !== removedId));
      setConfirmAction(null);
      setSelectedSMO(null);
      setWorkers([]);
    } catch (error) {
      console.error('Error removing SMO:', error);
      toast.error(labelRef.current('toast.smoRemoveError'));
    } finally {
      setRemoving(false);
    }
  }, [selectedSMO]);

  const sectionEmpty = <EmptyState compact icon={LuTriangleAlert} title={label('section.noVisits')} />;
  const smoFullName = selectedSMO?.name ? `${selectedSMO.name} ${selectedSMO.surname || ''}`.trim() : '';
  // Keeps the confirmation text while the dialog fades out after closing
  const shownConfirmRef = useRef(null);
  if (confirmAction) shownConfirmRef.current = confirmAction;
  const shownConfirm = confirmAction || shownConfirmRef.current;

  return (
    // `lang` keeps CSS upper-casing right in English (the document stays lang="tr")
    <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
      <SmoHeader
        showActions={!!selectedSMO && workers.length > 0}
        canManualEntry={canManualEntry}
        loadingWorkers={loadingWorkers}
        onManualEntry={() => setShowManualEntry(true)}
        onRefresh={fetchWorkersForSMO}
        onDownload={downloadExcel}
      />

      <SmoStats smoCount={smoList.length} selectedSMO={selectedSMO} workerCount={workers.length} />

      {/* SMO Selection Grid — admins (and users with several backup views) */}
      <SmoPicker
        isAdmin={isAdmin}
        loadingSMOs={loadingSMOs}
        smoList={smoList}
        employeeId={employeeId}
        delegatedSmoViews={delegatedSmoViews}
        centerBackupViews={centerBackupViews}
        selectedSMO={selectedSMO}
        onSelect={handleSMOClick}
      />

      {/* Workers Section - shown when an SMO is selected */}
      {selectedSMO && (
        <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
          <SmoCenters
            selectedSMO={selectedSMO}
            smoCenters={smoCenters}
            collapsedCities={collapsedCities}
            setCollapsedCities={setCollapsedCities}
            canRemoveSMO={canRemoveSMO}
            onRemoveSmo={() => setConfirmAction({ type: 'smo' })}
          />

          {workers.length > 0 && (
            <WorkerFilters
              workers={workers}
              incompleteWorkerCount={Object.keys(incompleteTasks.byWorker).length}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              primerFilter={primerFilter}
              setPrimerFilter={setPrimerFilter}
              blindedFilter={blindedFilter}
              setBlindedFilter={setBlindedFilter}
              hideFinished={hideFinished}
              setHideFinished={setHideFinished}
            />
          )}

          {/* Eksik zorunlu belgesi olan çalışanlar için tek satırlık özet uyarı */}
          {!loadingWorkers && (() => {
            const workersWithMissingDocs = filteredWorkers.filter(
              (w) => (missingDocsByWorker[w.id] || []).length > 0
            );
            if (workersWithMissingDocs.length === 0) return null;
            return (
              <div
                title={workersWithMissingDocs
                  .map((w) => `${w.name} ${w.surname}: ${(missingDocsByWorker[w.id] || []).map((d) => d.label).join(', ')}`)
                  .join('\n')}
              >
                <HelpNote tone="warning">{label('team.missingDocs', { count: workersWithMissingDocs.length })}</HelpNote>
              </div>
            );
          })()}

          {loadingWorkers ? (
            <p className="tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-10 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">
              <LuLoaderCircle aria-hidden="true" className="tw-h-5 tw-w-5 tw-animate-spin tw-text-teal-500" />
              {label('team.loading')}
            </p>
          ) : filteredWorkers.length > 0 ? (
            <WorkersTable
              filteredWorkers={filteredWorkers}
              expandedWorkers={expandedWorkers}
              toggleWorkerExpand={toggleWorkerExpand}
              missingDocsByWorker={missingDocsByWorker}
              incompleteVisitCounts={incompleteVisitCounts}
              canRemoveSMO={canRemoveSMO}
              onOpenDocuments={setDocumentsEmployee}
              onRemoveWorker={(worker) => setConfirmAction({ type: 'worker', worker })}
              tableContainerRef={tableContainerRef}
              handleScroll={handleScroll}
              showLeftScroll={showLeftScroll}
              showRightScroll={showRightScroll}
              scrollTable={scrollTable}
              detailsProps={{ statusFilter, incompleteTasks, incompleteVisitCounts, canRemoveSMO, onViewVisits: handleViewVisits }}
            />
          ) : (
            <EmptyState icon={LuUsers} title={searchTerm ? label('team.noMatch') : label('team.empty')} />
          )}
        </section>
      )}

      {/* ══════════════════════════════════════════════════════════════
          SECTION 1: Operasyonel Genel Bakış
          Story: "How is this SMO performing overall?"
          ══════════════════════════════════════════════════════════════ */}
      {selectedSMO && (
        <ReportSection
          icon={LuChartColumn}
          gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30"
          title={label('sections.overview.title')}
          description={label('sections.overview.text')}
          status={loadingVisits
            ? <SectionStatus>{label('section.loading')}</SectionStatus>
            : section1Summary && <SectionStatus>{label('sections.overview.status', { total: section1Summary.total, home: section1Summary.homeCount, site: section1Summary.siteCount })}</SectionStatus>}
          onExcel={!loadingVisits && section1Summary ? downloadCapaExcel : undefined}
          expanded={expandedSections.issueTracking}
          onToggle={() => toggleSection('issueTracking')}
          dateRange={<DateRangeBar id="smo-s1" start={s1DateStart} end={s1DateEnd} onStart={setS1DateStart} onEnd={setS1DateEnd} onThisMonth={() => resetDateFilter(1)} onAllTime={() => setAllTime(1)} />}
        >
          {!loadingVisits && !section1Summary && sectionEmpty}
          {!loadingVisits && section1Summary && expandedSections.issueTracking && (
            <OperationalOverview
              summary={section1Summary}
              visits={s1Visits}
              capaDashboard={capaDashboard}
              openIssueModal={openIssueModal}
              filterVisitsByMetric={filterVisitsByMetric}
            />
          )}
        </ReportSection>
      )}

      {/* ══════════════════════════════════════════════════════════════
          SECTION 2: Kalite & Klinik Risk Paneli
          Story: "What quality or compliance risks need attention?"
          ══════════════════════════════════════════════════════════════ */}
      {selectedSMO && !loadingVisits && (
        <ReportSection
          icon={LuTriangleAlert}
          gradient="tw-from-amber-400 tw-to-orange-600 tw-shadow-amber-500/30"
          title={label('sections.quality.title')}
          description={label('sections.quality.text')}
          status={section2Summary && <SectionStatus>{label('sections.quality.status', { count: section2Summary.total })}</SectionStatus>}
          onExcel={section2Summary ? downloadHastaneKpiExcel : undefined}
          expanded={expandedSections.hastaneKpi}
          onToggle={() => toggleSection('hastaneKpi')}
          dateRange={<DateRangeBar id="smo-s2" start={s2DateStart} end={s2DateEnd} onStart={setS2DateStart} onEnd={setS2DateEnd} onThisMonth={() => resetDateFilter(2)} onAllTime={() => setAllTime(2)} />}
        >
          {!section2Summary && sectionEmpty}
          {section2Summary && expandedSections.hastaneKpi && (
            <QualityRiskPanel summary={section2Summary} visits={s2Visits} capaDashboard={capaDashboard} openIssueModal={openIssueModal} />
          )}
        </ReportSection>
      )}

      {/* ══════════════════════════════════════════════════════════════
          SECTION 3: Ekip Performans Analizi
          Story: "How is each team member performing?"
          ══════════════════════════════════════════════════════════════ */}
      {selectedSMO && !loadingVisits && (
        <ReportSection
          icon={LuUsers}
          gradient="tw-from-teal-400 tw-to-emerald-600 tw-shadow-teal-500/30"
          title={label('sections.performance.title')}
          description={label('sections.performance.text')}
          status={employeeStats && <SectionStatus>{label('sections.performance.status', { count: employeeStats.stats.length })}</SectionStatus>}
          onExcel={employeeStats ? downloadEmployeeStatsExcel : undefined}
          expanded={expandedSections.employeePerf}
          onToggle={() => toggleSection('employeePerf')}
          dateRange={<DateRangeBar id="smo-s3" start={s3DateStart} end={s3DateEnd} onStart={setS3DateStart} onEnd={setS3DateEnd} onThisMonth={() => resetDateFilter(3)} onAllTime={() => setAllTime(3)} />}
        >
          {!employeeStats && sectionEmpty}
          {employeeStats && expandedSections.employeePerf && (
            <TeamPerformance
              employeeStats={employeeStats}
              visits={s3Visits}
              openIssueModal={openIssueModal}
              onOpenStats={(emp) => navigate('/employee-statistics', {
                state: {
                  employee: {
                    id: emp.id,
                    name: emp.name
                  },
                  employeeStats: emp, // Pass the entire employee stats object
                  visits: allVisits.filter(v => v.employeeId === emp.id), // Pass filtered visits
                  dateRange: {
                    start: s3DateStart,
                    end: s3DateEnd
                  }
                }
              })}
            />
          )}
        </ReportSection>
      )}

      {/* ══════════════════════════════════════════════════════════════
          SECTION 4: Ekip Sistem Kullanımı
          Story: "Who is online right now, and how much time does each
          team member actually spend in the system?"
          ══════════════════════════════════════════════════════════════ */}
      {selectedSMO && (
        <ReportSection
          icon={LuUserRoundCheck}
          gradient="tw-from-violet-400 tw-to-indigo-600 tw-shadow-violet-500/30"
          title={label('sections.activity.title')}
          description={label('sections.activity.text')}
          status={(
            <SectionStatus>
              <span aria-hidden="true" className={`tw-h-2 tw-w-2 tw-rounded-full ${teamActivity.summary.onlineCount > 0 ? 'tw-bg-emerald-500' : 'tw-bg-slate-400'}`} />
              {label('sections.activity.status', { online: teamActivity.summary.onlineCount, total: teamActivity.summary.teamSize })}
            </SectionStatus>
          )}
          expanded={expandedSections.teamActivity}
          onToggle={() => toggleSection('teamActivity')}
          dateRange={<DateRangeBar id="smo-s4" start={s4DateStart} end={s4DateEnd} onStart={setS4DateStart} onEnd={setS4DateEnd} onThisMonth={() => resetDateFilter(4)} onAllTime={() => setAllTime(4)} allTitle={label('dateRange.allRecorded')} />}
        >
          {expandedSections.teamActivity && (
            <TeamActivityPanel data={teamActivity} startDate={s4DateStart} endDate={s4DateEnd} smoName={smoFullName} />
          )}
        </ReportSection>
      )}

      {/* ══════════════════════════════════════════════════════════════
          SECTION 5: Eğitim ve SOP Takibi
          Story: "Who on my team still hasn't read their SOPs or finished
          their assigned trainings?"
          ══════════════════════════════════════════════════════════════ */}
      {selectedSMO && (
        <ReportSection
          icon={LuGraduationCap}
          gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30"
          title={label('sections.training.title')}
          description={label('sections.training.text')}
          status={teamTraining.summary.pendingPeople > 0 ? (
            <SectionStatus tone="alert">
              <LuTriangleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
              {label('sections.training.pending', { count: teamTraining.summary.pendingPeople })}
              {teamTraining.summary.overdue > 0 && ` · ${label('sections.training.overdue', { count: teamTraining.summary.overdue })}`}
            </SectionStatus>
          ) : (
            !teamTraining.loading && <SectionStatus>{label('sections.training.allDone')}</SectionStatus>
          )}
          expanded={expandedSections.teamTraining}
          onToggle={() => toggleSection('teamTraining')}
        >
          {expandedSections.teamTraining && <TeamTrainingPanel data={teamTraining} smoName={smoFullName} />}
        </ReportSection>
      )}

      {/* Issue Detail Modal — drill-down for any clicked issue indicator */}
      {issueModal && (
        <IssueDetailModal
          show={!!issueModal}
          onHide={() => setIssueModal(null)}
          title={issueModal.title}
          visits={issueModal.visits}
          workers={workers}
          projectNames={projectNameMap}
        />
      )}

      {/* CV/GCP/IATA belgeleri — ekipteki çalışanın belgelerini görüntüle */}
      <EmployeeDocumentsModal
        show={!!documentsEmployee}
        onHide={() => setDocumentsEmployee(null)}
        employee={documentsEmployee}
      />

      {/* Visits Modal */}
      {selectedEmployee && (
        <ProjectVisitsModal
          show={showVisitsModal}
          onHide={() => {
            setShowVisitsModal(false);
            setSelectedEmployee(null);
          }}
          project={selectedEmployee.project}
          employeeId={selectedEmployee.id}
          employeeName={selectedEmployee.name}
          visits={selectedEmployee.visits}
        />
      )}

      {/* Manual Data Entry Modal */}
      {canManualEntry && showManualEntry && (
        <ManualDataEntryModal
          show={showManualEntry}
          onHide={() => setShowManualEntry(false)}
          workers={workers}
          selectedSMO={selectedSMO}
          onDataSaved={() => {
            // Re-fetch visits to refresh dashboards
            if (workers.length > 0) {
              const fetchVisits = async () => {
                try {
                  const workerIds = workers.map(w => w.id);
                  const BATCH_SIZE = 30;
                  const batches = [];
                  for (let i = 0; i < workerIds.length; i += BATCH_SIZE) {
                    batches.push(workerIds.slice(i, i + BATCH_SIZE));
                  }
                  const results = await Promise.all(
                    batches.map(batch => {
                      const q = query(collection(db, 'Visits'), where('employeeId', 'in', batch));
                      return getDocs(q);
                    })
                  );
                  setAllVisits(results.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() }))));
                } catch (e) {
                  console.error('Error re-fetching visits:', e);
                }
              };
              fetchVisits();
            }
          }}
        />
      )}

      {/* Incomplete Tasks Modal */}
      <IncompleteTasksDialog
        isOpen={showIncompleteTasksModal}
        onClose={() => setShowIncompleteTasksModal(false)}
        incompleteTasks={incompleteTasks}
      />

      {/* Admin confirmation for removing a worker or an SMO */}
      <ConfirmDialog
        isOpen={!!confirmAction}
        tone="danger"
        title={shownConfirm?.type === 'worker' ? label('confirm.workerTitle') : label('confirm.smoTitle')}
        message={shownConfirm?.type === 'worker' ? (
          <>
            <p>{label('confirm.workerMessage', { worker: `${shownConfirm.worker.name} ${shownConfirm.worker.surname}`, smo: `${selectedSMO?.name} ${selectedSMO?.surname}` })}</p>
            <p className="tw-mt-2">{label('confirm.workerNote')}</p>
          </>
        ) : (
          <>
            <p>{label('confirm.smoMessage', { smo: `${selectedSMO?.name} ${selectedSMO?.surname}`, count: selectedSMO?.centerCount })}</p>
            <p className="tw-mt-2">{label('confirm.smoNote')}</p>
          </>
        )}
        confirmLabel={removing ? label('confirm.processing') : (shownConfirm?.type === 'worker' ? label('confirm.workerAction') : label('confirm.smoAction'))}
        cancelLabel={label('confirm.cancel')}
        busy={removing}
        onConfirm={() => (confirmAction.type === 'worker' ? removeWorkerFromSMO(confirmAction.worker) : removeSMO())}
        onCancel={() => !removing && setConfirmAction(null)}
      />
    </div>
  );
};

export default SMOLeadReports;
